export type CanonicalTreeGeometryBranchRoot =
  | 'maternal'
  | 'paternal'
  | 'both'
  | 'self'
  | 'descendant'
  | 'partner-affiliated'
  | 'child-mediated'
  | 'affiliated'
  | 'unresolved';

export interface CanonicalTreeGeometryBranchRootInput {
  readonly layoutPath:
    string | null | undefined;

  readonly semanticBranchPath?:
    string | null | undefined;

  readonly semanticAuthorityReady?:
    boolean;
}

export interface CanonicalTreeGeometryBranchRootResult {
  readonly root:
    CanonicalTreeGeometryBranchRoot;

  readonly source:
    | 'semantic-authority'
    | 'layout-compatibility'
    | 'unresolved';

  readonly layoutPath:
    string | null;

  readonly semanticBranchPath:
    string | null;
}

const semanticRootOf = (
  path:
    string | null | undefined,
): CanonicalTreeGeometryBranchRoot | null => {
  if (!path) return null;

  const root =
    path.split('.')[0];

  switch (root) {
    case 'maternal':
    case 'paternal':
    case 'both':
    case 'self':
    case 'descendant':
    case 'partner-affiliated':
    case 'child-mediated':
    case 'affiliated':
    case 'unresolved':
      return root;

    default:
      return null;
  }
};

const compatibilityRootOf = (
  path:
    string | null | undefined,
): CanonicalTreeGeometryBranchRoot | null => {
  if (!path) return null;

  if (path === 'self') {
    return 'self';
  }

  if (
    path === 'ancestor.0' ||
    path.startsWith('ancestor.0.')
  ) {
    return 'maternal';
  }

  if (
    path === 'ancestor.1' ||
    path.startsWith('ancestor.1.')
  ) {
    return 'paternal';
  }

  if (
    path === 'both' ||
    path.startsWith('both.')
  ) {
    return 'both';
  }

  if (
    path.includes('.child.') ||
    path.startsWith('descendant.')
  ) {
    return 'descendant';
  }

  if (
    path.endsWith('.partner') ||
    path.includes('.partner.')
  ) {
    return 'partner-affiliated';
  }

  if (
    path.startsWith('child-mediated.')
  ) {
    return 'child-mediated';
  }

  if (
    path.startsWith('affiliated.')
  ) {
    return 'affiliated';
  }

  if (
    path.startsWith('unresolved.')
  ) {
    return 'unresolved';
  }

  return null;
};

export const resolveCanonicalTreeGeometryBranchRoot = (
  input:
    CanonicalTreeGeometryBranchRootInput,
): CanonicalTreeGeometryBranchRootResult => {
  const layoutPath =
    input.layoutPath ?? null;

  const semanticBranchPath =
    input.semanticBranchPath ?? null;

  const semanticRoot =
    semanticRootOf(
      semanticBranchPath,
    );

  /*
   * CC-TREE-LIVE-003G3B
   *
   * Explicit unresolved canonical semantic evidence is a
   * fail-closed veto, not positive branch authority.
   *
   * Numeric compatibility paths may continue to provide
   * deterministic layout ordering, but they must not turn
   * known-indeterminate ancestry into maternal/paternal
   * semantic ownership.
   *
   * Positive semantic roots still require governed semantic
   * readiness, preserving the historical 003C1 migration
   * boundary.
   */
  if (
    semanticRoot ===
      'unresolved'
  ) {
    return Object.freeze({
      root:
        'unresolved',
      source:
        'unresolved',
      layoutPath,
      semanticBranchPath,
    });
  }

  if (
    input.semanticAuthorityReady &&
    semanticRoot
  ) {
    return Object.freeze({
      root:
        semanticRoot,
      source:
        'semantic-authority',
      layoutPath,
      semanticBranchPath,
    });
  }

  const compatibilityRoot =
    compatibilityRootOf(
      layoutPath,
    );

  if (compatibilityRoot) {
    return Object.freeze({
      root:
        compatibilityRoot,
      source:
        'layout-compatibility',
      layoutPath,
      semanticBranchPath,
    });
  }

  return Object.freeze({
    root:
      'unresolved',
    source:
      'unresolved',
    layoutPath,
    semanticBranchPath,
  });
};
